import React from 'react';
import { get } from 'lodash';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { useModal } from 'src/state/modal';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { config } from '@uhg-abyss/web/tools/config';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconPrimaryAdministrator } from 'src/common/IconPrimaryAdministrator';
import { Box } from '@uhg-abyss/web/ui/Box';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { AdminInfoList } from 'src/common/AdminInfoList';
import {
  ModalTitle,
  PaddedTitleBox,
  StyledLayout,
  PaddedBodyBox,
  PaddedText,
} from './PermissionsModalComponents';

const IconStyled = styled(IconPrimaryAdministrator, {
  position: 'relative',
  width: '200px',
  '& svg': {
    position: 'relative',
    width: '200px',
  },
});

const GainAccessButton = styled(Button, {
  padding: '0 10px 0',
});

const CloseButton = styled(Button, {
  marginTop: '15px',
  fontWeight: 'bold',
  verticalAlign: 'text-bottom',
});

const IconWrapper = styled('div', {
  width: '200px',
});

export const LimitedAccessModal = () => {
  const { modals } = useModal.state();
  const { user } = useUser.state();
  const { toggleModal } = useModal.actions();
  const isOrgAndTaxIDVisible = getFeature('ORG_TAXID_ACCESS_NAV_ITEM');
  const { getMessage } = usePPDLagoon();
  const gainAccessMessage = getMessage('oops-modal-limited-access-message');
  const handleClose = () => {
    toggleModal({ key: 'permissions', isOpen: false });
  };

  const userPermissionsFailed = user.isUserApiError
    ? user.isUserApiError
    : false;

  const adminListProps = {
    ...user.adminInfo,
    isMaskInfo: userPermissionsFailed,
  };

  const isAdminInfo =
    get(user, 'adminInfo.adminFirstName') ||
    get(user, 'adminInfo.adminLastName') ||
    get(user, 'adminInfo.adminEmail');

  return (
    <Modal
      id="gain-access-modal"
      data-testid="gain-access-modal"
      model="customization"
      isOpen={modals.permissions}
      onClose={handleClose}
      size={700}
    >
      <Modal.Section>
        <PaddedTitleBox color="white">
          <Layout.Stack alignLayout="center" space={16}>
            <ModalTitle textAlign="center" offset={2}>
              Oops! It looks like you don't have access to {modals.toolName}{' '}
              with the TIN you have selected in the provider selection
              drop-down.
            </ModalTitle>
          </Layout.Stack>
        </PaddedTitleBox>

        <Choose>
          <When condition={isAdminInfo || !isOrgAndTaxIDVisible}>
            <Box color="white">
              <StyledLayout space={16}>
                <Flex justify="center">
                  <PaddedText>{gainAccessMessage}</PaddedText>
                </Flex>
                <AdminInfoList {...adminListProps} />
              </StyledLayout>
            </Box>
          </When>
          <Otherwise>
            <PaddedBodyBox color="white">
              <Layout.Group>
                <PaddedText>
                  {gainAccessMessage}
                  {'  '}
                  <a
                    data-test-id="org-and-tax-id-access-page"
                    href="#/prefcenter/orgtaxIdAccess"
                    target="_blank"
                    rel="noopener"
                  >
                    Org and Tax ID access page
                  </a>
                  {'  '}. When your access has been granted, you will be
                  notified via email.
                </PaddedText>
                <IconWrapper>
                  <IconStyled size={24} />
                </IconWrapper>
              </Layout.Group>
            </PaddedBodyBox>

            <StyledLayout alignLayout="center" alignItems="center">
              <GainAccessButton
                id="go-to-org-and-tax-id-access-page"
                aria-label="Click to Go to Org and Tax ID access page"
                onClick={() => window.open(config('ORG_AND_TAX_ID_ACCESS_URL'))}
              >
                Go to Org and Tax ID access page
              </GainAccessButton>
            </StyledLayout>
          </Otherwise>
        </Choose>

        <If condition={!user.adminInfo && isOrgAndTaxIDVisible}>
          <StyledLayout alignLayout="center" alignItems="center">
            <CloseButton
              id="close-btn"
              aria-label="Click to close Permissions modal"
              onClick={handleClose}
              size="$lg"
            >
              Close
            </CloseButton>
          </StyledLayout>
        </If>
      </Modal.Section>
    </Modal>
  );
};
